/**
 * Client-side education content module.
 *
 * Bundles the static education summaries directly into the frontend for:
 * - Instant display (no API round trip)
 * - Full offline support
 * - ~34KB gzipped, well within acceptable bundle size
 *
 * The content here mirrors packages/clinical-engine/src/education-content.ts.
 * If the clinical engine content is updated, this file should be regenerated.
 *
 * Each entry's text — title and summary — lives in `src/content/education/<language>.json`,
 * one file per language, keyed by slug; everything else about an entry (its
 * citations, triggers, review date) is here and is the same in every language.
 * Citations stay in English. A language's entries are shown only when its file has
 * every entry (`hasEducationContent`); until then the reader gets English, and
 * i18n.ts does not offer the language (lib/contentCompleteness.ts).
 *
 * The generateContextHeader() function runs client-side using data already
 * available in the frontend state (cat name, stage, current lab values), with its
 * sentences in the locale file under `education.contextHeader`.
 */

import i18next from 'i18next';
import { formatNumber } from './format';
import { DEFAULT_LANGUAGE, isLanguage } from './language';
import { pronounContext } from './pronouns';
import { educationTextFor, hasEducationContent } from './educationText';

export { hasEducationContent };

// ===== EVIDENCE REFERENCES =====

export const EVIDENCE_REFERENCES: Record<string, string> = {
  iris_staging: 'IRIS CKD Staging Guidelines (2026)',
  iris_treatment: 'IRIS Treatment Recommendations for Cats (2026)',
  isfm_guidelines:
    'ISFM Consensus Guidelines on Diagnosis and Management of Feline CKD (Sparkes et al., JFMS 2016)',
  phosphorus_dietary:
    'Elliott et al. 2000 (JSAP) — Dietary management of feline CKD',
  phosphorus_binders:
    'Kidder & Chew 2009 (JFMS) — Phosphate binder review',
  telmisartan_benazepril:
    'Sent et al. 2015 (JVIM) — Telmisartan vs benazepril comparison',
  fgf23_marker:
    'Finch et al. 2013 (JVIM) — FGF-23 rises before azotemia',
  sdma_validation: 'Hall et al. 2014 (JVIM) — SDMA validation',
  roudebush_therapies:
    'Roudebush et al. 2009 (JFMS) — Therapies for Feline CKD: What is the Evidence?',
  ross_dietary: 'Ross et al. 2006 (JAVMA) — Dietary management',
  brown_phosphate: 'Brown et al. 2008 — Phosphate binder efficacy',
  quimby_maropitant:
    'Quimby et al. 2015 (JFMS) — Maropitant for CKD nausea',
  bijsmans_catqol:
    'Bijsmans et al. 2016 — CatQoL validated quality of life tool',
  quimby_gabapentin_bp:
    'Quimby JM, Jones SE, Saffire A, et al. (2024) Assessment of the effect of gabapentin on blood pressure in cats with and without CKD. JFMS.',
  thyroid_ckd_masking:
    'Syme HM. (2007) Cardiovascular and renal manifestations of hyperthyroidism. VCNA Small Animal Practice.',
};

// ===== TYPES =====

export interface EducationEntry {
  slug: string;
  title: string;
  summary: string;
  citation_keys: string[];
  triggered_by: string[];
  version: number;
  last_reviewed: string;
  confidence_notes: string;
}

// ===== CONTENT =====

type EducationMeta = Omit<EducationEntry, 'slug' | 'title' | 'summary'>;

const EDUCATION_META: Record<string, EducationMeta> = {
  iris_staging: {
    citation_keys: ['iris_staging', 'sdma_validation', 'isfm_guidelines'],
    triggered_by: ['iris_stage'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: '',
  },
  phosphorus_management: {
    citation_keys: ['phosphorus_dietary', 'phosphorus_binders', 'fgf23_marker', 'iris_treatment', 'isfm_guidelines'],
    triggered_by: ['phosphorus'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: '',
  },
  hydration: {
    citation_keys: ['iris_treatment', 'isfm_guidelines'],
    triggered_by: ['hydration'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: '',
  },
  blood_pressure: {
    citation_keys: ['iris_treatment', 'isfm_guidelines'],
    triggered_by: ['systolic_bp', 'blood_pressure'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: "The 20–65% prevalence range for hypertension in CKD cats is widely cited in the ISFM guidelines (Sparkes 2016) and IRIS documentation. The range is broad because study populations and measurement methods vary.",
  },
  proteinuria: {
    citation_keys: ['iris_treatment', 'telmisartan_benazepril', 'isfm_guidelines'],
    triggered_by: ['upc_ratio', 'proteinuria'],
    version: 2,
    last_reviewed: '2026-05-11',
    confidence_notes: "v2: Corrected causal mechanism claim. The association between proteinuria and faster CKD progression in cats is well-established, but the specific mechanistic pathway (tubular inflammation/scarring) is extrapolated from human and experimental models, not directly demonstrated in feline studies.",
  },
  potassium_balance: {
    citation_keys: ['iris_treatment', 'isfm_guidelines'],
    triggered_by: ['potassium'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: '',
  },
  anemia: {
    citation_keys: ['iris_treatment', 'isfm_guidelines'],
    triggered_by: ['pcv_haematocrit', 'anemia'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: '',
  },
  renal_diet: {
    citation_keys: ['ross_dietary', 'phosphorus_dietary', 'roudebush_therapies', 'iris_treatment', 'isfm_guidelines'],
    triggered_by: ['nutrition', 'renal_diet'],
    version: 2,
    last_reviewed: '2026-05-11',
    confidence_notes: "v2: Corrected attribution of 633 vs 264 day survival figures from Ross et al. (2006) to Elliott et al. (2000). Ross et al. (2006) findings restated as uremic crisis/mortality outcomes from a separate RCT. Both studies cited correctly.",
  },
  metabolic_acidosis: {
    citation_keys: ['iris_treatment', 'isfm_guidelines'],
    triggered_by: ['bicarbonate_tco2'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: "The recommendation for potassium citrate as first-line alkaliniser in cats is well-established in clinical practice and IRIS guidelines.",
  },
  calcium_balance: {
    citation_keys: ['iris_treatment', 'isfm_guidelines', 'phosphorus_binders'],
    triggered_by: ['calcium_ionized', 'calcium_total'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: "The relationship between idiopathic hypercalcemia and CKD in cats is an area of active research. Whether hypercalcemia causes CKD or is a comorbidity is not fully resolved. Summary reflects IRIS and ISFM consensus without overclaiming causation.",
  },
  thyroid_ckd_interaction: {
    citation_keys: ['thyroid_ckd_masking', 'iris_staging', 'iris_treatment'],
    triggered_by: ['creatinine_thyroid'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: '',
  },
  weight_loss_in_ckd: {
    citation_keys: ['iris_treatment', 'isfm_guidelines', 'quimby_maropitant'],
    triggered_by: ['weight'],
    version: 2,
    last_reviewed: '2026-05-11',
    confidence_notes: "v2: Added note about Stage 4 threshold difference to align with constants.ts (WEIGHT_LOSS_THRESHOLD_PERCENT_STAGE4 = 10%).",
  },
  quality_of_life_monitoring: {
    citation_keys: ['bijsmans_catqol', 'isfm_guidelines'],
    triggered_by: ['qol_composite'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: '',
  },
  fluid_therapy_at_home: {
    citation_keys: ['iris_treatment', 'isfm_guidelines'],
    triggered_by: ['hydration_advanced', 'fluid_therapy'],
    version: 1,
    last_reviewed: '2025-05-11',
    confidence_notes: '',
  },
};

/** The education entries in a language, English where that language is incomplete. */
export function educationContent(lang: string = i18next.language ?? DEFAULT_LANGUAGE): Record<string, EducationEntry> {
  const language = isLanguage(lang) && hasEducationContent(lang) ? lang : DEFAULT_LANGUAGE;
  const text = educationTextFor(language)!;
  return Object.fromEntries(
    Object.entries(EDUCATION_META).map(([slug, meta]) => [slug, { slug, ...text[slug], ...meta }]),
  );
}

/** The English entries — the source every translation is made from. */
export const EDUCATION_CONTENT: Record<string, EducationEntry> = educationContent(DEFAULT_LANGUAGE);

// ===== HELPERS =====

/**
 * Generate a personalised context header for an education entry.
 * Runs client-side using data from the cat store and recent lab values.
 */
export function generateContextHeader(
  catName: string | null,
  irisStage: number,
  parameterContext?: {
    value: number;
    unit: string;
    target_max?: number;
    target_min?: number;
  },
  catSex?: 'male' | 'female' | null,
): string {
  let header = catName
    ? i18next.t('education.contextHeader.stage', { catName, stage: irisStage })
    : i18next.t('education.contextHeader.stageNoName', { stage: irisStage });

  if (parameterContext) {
    const { value, unit, target_max, target_min } = parameterContext;
    const context = pronounContext(catSex);
    const reading = { value: formatNumber(value), unit, stage: irisStage, context };

    if (target_max !== undefined && value > target_max) {
      header += ` ${i18next.t('education.contextHeader.aboveTarget', { ...reading, max: formatNumber(target_max) })}`;
    } else if (target_min !== undefined && value < target_min) {
      header += ` ${i18next.t('education.contextHeader.belowTarget', { ...reading, min: formatNumber(target_min) })}`;
    } else {
      header += ` ${i18next.t('education.contextHeader.reading', reading)}`;
    }
  }

  return header;
}

/**
 * Format full citations from citation keys.
 */
export function getFullCitations(citationKeys: string[]): string[] {
  return citationKeys
    .map((key) => EVIDENCE_REFERENCES[key])
    .filter(Boolean);
}
